import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as RetentionIcon,
  PersonOff as LostIcon,
  PersonAdd as NewIcon,
  SwapHoriz as ChangeIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';

const CHART_COLORS = ['#2E4156', '#5A7A95'];

const StatCard = ({ title, value, subtitle, icon, color = 'primary.main' }) => (
  <Card variant="outlined" sx={{ flex: '1 1 200px', minWidth: 0 }}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ color, display: 'flex', alignItems: 'center' }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" fontWeight={700} lineHeight={1.2}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </CardContent>
  </Card>
);

const PlayerTable = ({ players, title, showPreviousRole, showCurrentRole, emptyMessage }) => {
  if (!players || players.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">{emptyMessage || 'No players found.'}</Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined">
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={600}>
          {title} ({players.length})
        </Typography>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              {showPreviousRole && <TableCell>Previous Role</TableCell>}
              {showCurrentRole && <TableCell>Current Role</TableCell>}
              <TableCell>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {players.map((player, idx) => (
              <TableRow key={player.userTenantId || player.email || idx}>
                <TableCell>{player.name}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {player.email || '—'}
                  </Typography>
                </TableCell>
                {showPreviousRole && (
                  <TableCell>
                    {player.previousRole ? (
                      <Chip label={player.previousRole} size="small" variant="outlined" clickable={false} onClick={() => {}} />
                    ) : '—'}
                  </TableCell>
                )}
                {showCurrentRole && (
                  <TableCell>
                    {player.currentRole ? (
                      <Chip label={player.currentRole} size="small" color={player.currentRole === 'Regular' ? 'primary' : 'default'} clickable={false} onClick={() => {}} />
                    ) : '—'}
                  </TableCell>
                )}
                <TableCell>
                  <Chip label={player.source} size="small" variant="outlined" clickable={false} onClick={() => {}} sx={{ fontSize: '0.7rem' }} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};

const RosterCompositionChart = ({ current, previous, currentName, previousName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const currentData = [
    { name: 'Regular', value: current.regularCount },
    { name: 'Spare', value: current.spareCount },
  ].filter(d => d.value > 0);

  const previousData = [
    { name: 'Regular', value: previous.regularCount },
    { name: 'Spare', value: previous.spareCount },
  ].filter(d => d.value > 0);

  if (currentData.length === 0 && previousData.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Roster Composition
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
        {previousData.length > 0 && (
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>{previousName}</Typography>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={previousData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, value }) => `${name}: ${value}`}>
                  {previousData.map((_, index) => (
                    <Cell key={`prev-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        )}
        {currentData.length > 0 && (
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>{currentName}</Typography>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={currentData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, value }) => `${name}: ${value}`}>
                  {currentData.map((_, index) => (
                    <Cell key={`curr-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

const SeasonComparisonPage = () => {
  const [leagues, setLeagues] = useState([]);
  const [currentLeagueId, setCurrentLeagueId] = useState('');
  const [previousLeagueId, setPreviousLeagueId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leaguesLoading, setLeaguesLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        const data = await unifiedApi.getLeagues();
        if (data) {
          setLeagues(data);
        }
      } catch (err) {
        setError('Failed to load leagues.');
      } finally {
        setLeaguesLoading(false);
      }
    };
    fetchLeagues();
  }, []);

  const handleCompare = useCallback(async () => {
    if (!currentLeagueId || !previousLeagueId) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await unifiedApi.getSeasonComparison(currentLeagueId, previousLeagueId);
      if (data) {
        setResult(data);
      } else {
        setError('No comparison data returned.');
      }
    } catch (err) {
      setError(err.message || 'Failed to load comparison.');
    } finally {
      setLoading(false);
    }
  }, [currentLeagueId, previousLeagueId]);

  const canCompare = currentLeagueId && previousLeagueId && currentLeagueId !== previousLeagueId;

  return (
    <AdminLayout>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Button
            component={Link}
            to="/admin/reports"
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 1, textTransform: 'none' }}
          >
            Back to Reports
          </Button>
          <Typography variant="h4" component="h1" fontWeight={700}>
            Season Comparison
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Compare player rosters between two seasons to analyze retention and changes.
          </Typography>
        </Box>

        {/* League Selectors */}
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
            <FormControl sx={{ minWidth: 220, flex: 1 }} size="small">
              <InputLabel>Current Season</InputLabel>
              <Select
                value={currentLeagueId}
                label="Current Season"
                onChange={(e) => setCurrentLeagueId(e.target.value)}
                disabled={leaguesLoading}
              >
                {leagues.map((league) => (
                  <MenuItem key={league.id} value={league.id} disabled={league.id === previousLeagueId}>
                    {league.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 220, flex: 1 }} size="small">
              <InputLabel>Previous Season</InputLabel>
              <Select
                value={previousLeagueId}
                label="Previous Season"
                onChange={(e) => setPreviousLeagueId(e.target.value)}
                disabled={leaguesLoading}
              >
                {leagues.map((league) => (
                  <MenuItem key={league.id} value={league.id} disabled={league.id === currentLeagueId}>
                    {league.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleCompare}
              disabled={!canCompare || loading}
              sx={{ minWidth: 120 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Compare'}
            </Button>
          </Stack>
        </Paper>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Results */}
        {result && !loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Summary Stat Cards */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <StatCard
                title="Retention Rate"
                value={`${result.summary.retentionRate}%`}
                subtitle={`${result.summary.previousSeasonTotal - result.summary.playersLost} of ${result.summary.previousSeasonTotal} returned`}
                icon={<RetentionIcon fontSize="large" />}
                color="success.main"
              />
              <StatCard
                title="Players Lost"
                value={result.summary.playersLost}
                subtitle="Did not return this season"
                icon={<LostIcon fontSize="large" />}
                color="error.main"
              />
              <StatCard
                title="New Players"
                value={result.summary.newPlayersCount}
                subtitle="Not in previous season"
                icon={<NewIcon fontSize="large" />}
                color="info.main"
              />
              <StatCard
                title="Role Changes"
                value={result.summary.roleChangesCount}
                subtitle="Changed between Regular/Spare"
                icon={<ChangeIcon fontSize="large" />}
                color="warning.main"
              />
            </Box>

            {/* Roster Composition Charts */}
            <RosterCompositionChart
              current={result.currentRosterComposition}
              previous={result.previousRosterComposition}
              currentName={result.currentSeason.leagueName}
              previousName={result.previousSeason.leagueName}
            />

            {/* Player Lists */}
            <PlayerTable
              players={result.didNotReturn}
              title="Did Not Return"
              showPreviousRole
              showCurrentRole={false}
              emptyMessage="All players from the previous season returned."
            />

            <PlayerTable
              players={result.roleChanges}
              title="Role Changes"
              showPreviousRole
              showCurrentRole
              emptyMessage="No players changed roles between seasons."
            />

            <PlayerTable
              players={result.newPlayers}
              title="New Players"
              showPreviousRole={false}
              showCurrentRole
              emptyMessage="No new players this season."
            />

            <PlayerTable
              players={result.returningPlayers}
              title="Returning Players"
              showPreviousRole
              showCurrentRole
              emptyMessage="No returning players found."
            />
          </Box>
        )}
      </Box>
    </AdminLayout>
  );
};

export default SeasonComparisonPage;
